import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { LayoutLevel } from '../../features/layoutSlice';
import { fontSize } from '../../common/themes';

// 配置常數
const SCALE_CONFIG = {
	windowSizes: {
		small: { width: 360, height: 580 },
		medium: { width: 468, height: 754 },
		large: { width: 576, height: 928 },
	},
	windowScales: { small: 1, medium: 1.3, large: 1.6 },
	// 字級縮放倍率：以 small 為基準等比放大，套用到 themes 的 xs/sm/md/lg
	fontScales: { small: 1, medium: 1.2, large: 1.45 },
} as const;

export interface ScaledFontSizes {
	xs: number;
	sm: number;
	md: number;
	lg: number;
}

export interface ResponsiveScale {
	fontSize: number;
	fontScale: number;
	fontSizes: ScaledFontSizes;
	windowScale: number;
	windowSize: { width: number; height: number };
}

// 純函式：依 level 計算實際縮放值，方便測試與重複使用
export const computeResponsiveScale = (
	fontSizeLevel: LayoutLevel = 'small',
	windowSizeLevel: LayoutLevel = 'small'
): ResponsiveScale => {
	const fontScale = SCALE_CONFIG.fontScales[fontSizeLevel] ?? SCALE_CONFIG.fontScales.small;
	const windowScale = SCALE_CONFIG.windowScales[windowSizeLevel] ?? SCALE_CONFIG.windowScales.small;
	const windowSize = SCALE_CONFIG.windowSizes[windowSizeLevel] ?? SCALE_CONFIG.windowSizes.small;

	const fontSizes: ScaledFontSizes = {
		xs: fontSize.xs * fontScale,
		sm: fontSize.sm * fontScale,
		md: fontSize.md * fontScale,
		lg: fontSize.lg * fontScale,
	};

	return {
		fontSize: fontSizes.md,
		fontScale,
		fontSizes,
		windowScale,
		windowSize,
	};
};

const useResponsiveScale = (): ResponsiveScale => {
	// 從 Redux 讀取配置，提供預設值防止未初始化錯誤
	const windowSizeLevel = useSelector((state: any) => state.layout?.windowSizeLevel || 'small');
	const fontSizeLevel = useSelector((state: any) => state.layout?.fontSizeLevel || 'small');

	const scale = computeResponsiveScale(fontSizeLevel as LayoutLevel, windowSizeLevel as LayoutLevel);

	// 同步 CSS 變數，供少數以 calc() 取用的樣式（例如 1rem 基準字級、視窗縮放）使用
	useEffect(() => {
		document.documentElement.style.setProperty('--livechat-font-scale', String(scale.fontScale));
		document.documentElement.style.setProperty('--livechat-window-scale', String(scale.windowScale));
	}, [scale.fontScale, scale.windowScale]);

	return scale;
};

export default useResponsiveScale;
